import { useState } from 'react';
import type { LinkFiledStatementsResult } from '@accounting/shared';
import {
  useLinkFiledStatements,
  useLinkFiledStatementsPreview,
} from '../../hooks/useReconciliation';
import { ApiError } from '../../lib/api';
import { formatAccountingDate } from '../../lib/formatters';

function apiErrorMessage(err: unknown): string | undefined {
  if (err instanceof ApiError) {
    return (err.body as { error?: string } | null)?.error ?? 'Request failed';
  }
  return err instanceof Error ? err.message : undefined;
}

/**
 * Pair the statements already uploaded through Documents with the completed
 * reconciliations that have nothing behind them, and re-file those packets.
 *
 * Two steps on purpose: the preview shows every pairing and every
 * reconciliation it could not resolve, and nothing is written until the
 * owner has read that list. A statement attached to the wrong reconciliation
 * is an evidence problem, not a cosmetic one.
 */
export function LinkFiledStatementsPanel({
  companyId,
  onClose,
}: {
  companyId: string;
  onClose: () => void;
}) {
  const preview = useLinkFiledStatementsPreview(companyId);
  const link = useLinkFiledStatements(companyId);
  const [done, setDone] = useState<LinkFiledStatementsResult | null>(null);

  const result = done ?? preview.data ?? null;
  const error = apiErrorMessage(link.error) ?? apiErrorMessage(preview.error);

  return (
    <div className="card" style={{ marginBottom: 16, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ fontSize: 16, margin: 0 }}>Link filed statements</h2>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
            Statements uploaded through Documents are paired with completed reconciliations by
            statement date, then by the account number in the filename. Each paired packet is
            re-filed with the statement behind the proof.
          </p>
        </div>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Close
        </button>
      </div>

      {preview.isLoading && (
        <p className="muted" style={{ fontSize: 13, marginTop: 12 }}>
          Checking…
        </p>
      )}
      {error && (
        <div className="field-error" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}

      {result && (
        <div style={{ marginTop: 12 }}>
          {result.linked.length === 0 && result.unresolved.length === 0 && (
            <p className="tag ok" style={{ display: 'block', padding: 8 }}>
              Every completed reconciliation already has its statement.
            </p>
          )}

          {result.linked.length > 0 && (
            <>
              <h3 style={{ fontSize: 14, margin: '0 0 6px' }}>
                {done ? 'Linked' : 'Will link'} ({result.linked.length})
              </h3>
              <table style={{ width: '100%', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Account</th>
                    <th style={{ textAlign: 'left' }}>Statement</th>
                    <th style={{ textAlign: 'left' }}>File</th>
                    {done && <th style={{ textAlign: 'left' }}>Packet</th>}
                  </tr>
                </thead>
                <tbody>
                  {result.linked.map((l) => (
                    <tr key={l.reconciliationId}>
                      <td>
                        {l.glAccountCode} {l.glAccountName}
                      </td>
                      <td>{formatAccountingDate(l.statementDate)}</td>
                      <td>{l.fileName}</td>
                      {done && (
                        <td>
                          {l.packetRefiled ? (
                            <span className="tag ok">re-filed</span>
                          ) : (
                            <span className="tag warn">not re-filed</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {result.unresolved.length > 0 && (
            <>
              <h3 style={{ fontSize: 14, margin: '12px 0 6px' }}>
                Still without a statement ({result.unresolved.length})
              </h3>
              <p className="muted" style={{ fontSize: 12, margin: '0 0 6px' }}>
                Open each reconciliation and drop its statement on the &ldquo;No statement on
                file&rdquo; zone.
              </p>
              <table style={{ width: '100%', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Account</th>
                    <th style={{ textAlign: 'left' }}>Statement</th>
                    <th style={{ textAlign: 'left' }}>Why</th>
                  </tr>
                </thead>
                <tbody>
                  {result.unresolved.map((u) => (
                    <tr key={u.reconciliationId}>
                      <td>
                        {u.glAccountCode} {u.glAccountName}
                      </td>
                      <td>{formatAccountingDate(u.statementDate)}</td>
                      <td className="muted">{u.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {!done && result.linked.length > 0 && (
            <button
              type="button"
              className="btn-primary"
              style={{ marginTop: 12 }}
              disabled={link.isPending}
              onClick={() => link.mutate(undefined, { onSuccess: setDone })}
            >
              {link.isPending
                ? 'Linking…'
                : `Link ${result.linked.length} statement${result.linked.length === 1 ? '' : 's'} and re-file`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
